import type {ComponentType} from 'svelte';
import {derived, get, writable} from 'svelte/store';
import {
  onCloseState,
  completePaymentStage,
} from 'Store/ibex/checkouts/investorCheckoutStore';

export enum InvestorCheckoutState {
  PRE_CHECKOUT = 'pre_checkout',
  INVESTMENT_AMOUNT = 'investment_amount',
  CONTACT_INFORMATION = 'contact_information',
  INVESTOR_CONFIRMATION = 'investor_confirmation',
  TERMS_CONDITIONS = 'terms_conditions',
  PAYMENT = 'payment',
  CHECKOUT_COMPLETE = 'checkout_complete',
  AGREEMENT_RESUBMISSION = 'resubmit_agreement',
  DOCUMENT_UPLOAD = 'document_upload',
}

export enum SectionsId {
  STAGE_INVESTMENT_AMOUNT = 'investment_amount',
  STAGE_CONTACT_INFORMATION = 'contact_information',
  STAGE_INVESTOR_CONFIRMATION = 'investor_confirmation',
  STAGE_TERMS_CONDITIONS = 'terms_conditions',
  STAGE_PAYMENT = 'payment',
  STAGE_AGREEMENT_RESUBMISSION = 'resubmit_agreement',
  STAGE_DOCUMENT_UPLOAD = 'document_upload',
  STAGE_VERIFICATIONS = 'verifications',
}

export enum SectionsTitle {
  STAGE_INVESTMENT_AMOUNT = 'Investment Amount',
  STAGE_CONTACT_INFORMATION = 'Contact Information',
  STAGE_INVESTOR_CONFIRMATION = 'Investor Confirmation',
  STAGE_TERMS_CONDITIONS = 'Terms & Conditions',
  STAGE_PAYMENT = 'Payment',
  STAGE_AGREEMENT_RESUBMISSION = 'Agreement Submission',
  STAGE_DOCUMENT_UPLOAD = 'Documents',
  STAGE_VERIFICATIONS = 'Verifications',
  STAGE_SIGN_AGREEMENT = 'Sign Agreement',
}

export type Section = {
  id: string;
  title: string;
  component: ComponentType;
  opened: boolean;
  finished: boolean;
  disabled?: boolean;
};

const createSectionStore = () => {
  const sections = writable<Section[]>([]);

  const {subscribe, set, update} = sections;

  const closeAllSections = (): void => {
    update(section => {
      return section.map(sec => {
        return {...sec, opened: false};
      });
    });
  };

  const updateSection = (currentSection: Section, data: Object): void => {
    update(section => {
      return section.map(sec => {
        if (sec.id === currentSection.id) {
          return {...currentSection, ...data};
        }

        return {...sec, opened: false};
      });
    });
  };

  /**
   * Marks all sections after and including sectionId as not finished
   * Will not mark payment section as not finished, due to re-sign functionality with clickthrough investing.
   */
  const resetSection = (sectionId: SectionsId): void => {
    update(sections => {
      const currentSection = sections.find(section => section.id === sectionId);

      const nextSecs = nextSections(currentSection);

      return sections.map(sec => {
        if (
          sec.id !== SectionsId.STAGE_PAYMENT &&
          (sec.id === currentSection.id ||
            nextSecs.find(ns => ns.id === sec.id))
        ) {
          if (
            sectionId === SectionsId.STAGE_INVESTOR_CONFIRMATION ||
            sectionId === SectionsId.STAGE_VERIFICATIONS
          ) {
            if (sectionId === sec.id) {
              return {...sec, finished: false, opened: true};
            } else {
              return {...sec, finished: false, opened: false};
            }
          }
          return {...sec, finished: false};
        }
        return sec;
      });
    });
  };

  const resetContactInformation = (): void => {
    resetSection(SectionsId.STAGE_CONTACT_INFORMATION);
  };

  const resetInvestorConfirmation = (): void => {
    const allSections = getAllSections();
    const verificationSection = allSections.find(
      section => section.id === SectionsId.STAGE_VERIFICATIONS
    );

    if (verificationSection) {
      resetSection(SectionsId.STAGE_VERIFICATIONS);
    } else {
      resetSection(SectionsId.STAGE_INVESTOR_CONFIRMATION);
    }
  };

  const nextSections = (section: Section): Section[] => {
    const allSections = getAllSections();
    const idxSection = allSections.findIndex(sec => {
      return sec.id === section.id;
    });
    return allSections.filter((_section, idx) => idx > idxSection);
  };

  const previousSections = (section: Section): Section[] => {
    const allSections = getAllSections();
    const idxSection = allSections.findIndex(sec => {
      return sec.id === section.id;
    });
    return allSections.filter((_section, idx) => idx < idxSection);
  };

  const sectionsBetween = (from: Section, to: Section): Section[] => {
    const allSections = getAllSections();
    const idxFrom = allSections.findIndex(sec => {
      return sec.id === from.id;
    });
    const idxTo = allSections.findIndex(sec => {
      return sec.id === to.id;
    });
    return allSections.filter((_section, idx) => idx > idxFrom && idx < idxTo);
  };

  const getAllSections = (): Section[] => {
    return get(sections);
  };

  const findSectionById = (id: string): Section => {
    const allSections = getAllSections();
    return allSections.find(section => section.id === id);
  };

  return {
    subscribe,
    set,
    update,
    closeAllSections,
    updateSection,
    nextSections,
    previousSections,
    sectionsBetween,
    getAllSections,
    findSectionById,
    resetContactInformation,
    resetInvestorConfirmation,
  };
};

export const sectionsStore = createSectionStore();

export const mappedSections = derived(
  sectionsStore,
  ($sections): {[key in SectionsId]?: Section} => {
    return $sections.reduce((obj, section) => {
      return {...obj, [section.id]: section};
    }, {});
  }
);

export const closePaymentSection = async () => {
  const section = mappedSections[SectionsId.STAGE_PAYMENT];
  onCloseState(section);
  await completePaymentStage();
};

export const checkoutPendingStepsCount = derived(
  sectionsStore,
  ($sections = []) =>
    ($sections ?? []).reduce(
      (acc, section) => (section.finished ? acc : ++acc),
      0
    )
);
